<template>
  <main class="property-detail-page">
    <section v-if="loading" class="detail-shell detail-loading" aria-label="A carregar imóvel">
      <div class="loading-gallery"></div>
      <div class="loading-grid">
        <div class="loading-panel"></div>
        <div class="loading-panel loading-panel--side"></div>
      </div>
    </section>

    <section v-else-if="errorMessage" class="detail-shell detail-error" role="alert">
      <p>{{ errorMessage }}</p>
      <button type="button" @click="loadProperty">Tentar novamente</button>
    </section>

    <template v-else>
      <nav class="detail-shell breadcrumbs" aria-label="Navegação estrutural">
        <button type="button" @click="voltarLista">Imóveis</button>
        <span aria-hidden="true">/</span>
        <span>{{ imovel.title }}</span>
      </nav>

      <section
        class="detail-shell gallery-section"
        :class="{ 'gallery-section--single': thumbnailImages.length === 0 }"
        aria-label="Galeria do imóvel"
      >
        <button class="gallery-main" type="button" @click="abrirGaleria(imagemAtual)">
          <img :src="imagemPrincipal" :alt="imovel.title" />
          <span class="gallery-badges">
            <span v-if="promotionLabel" class="promotion-badge">{{ promotionLabel }}</span>
            <span v-if="imovel.objective">{{ imovel.objective }}</span>
            <span v-if="imovel.condition" class="gallery-badge--light">{{ imovel.condition }}</span>
          </span>
          <span v-if="images.length > 1" class="gallery-arrows" aria-hidden="true">
            <span @click.stop="imagemAnterior">‹</span>
            <span @click.stop="proximaImagem">›</span>
          </span>
        </button>

        <div v-if="thumbnailImages.length" class="gallery-thumbnails">
          <button
            v-for="(image, index) in thumbnailImages"
            :key="`${image}-${index}`"
            type="button"
            :class="{ 'is-active': imagemAtual === index + 1 }"
            @click="selecionarImagem(index + 1)"
          >
            <img :src="image" :alt="`${imovel.title} — fotografia ${index + 2}`" />
            <span v-if="index === thumbnailImages.length - 1 && images.length > 5" class="gallery-more">
              Ver todas ({{ images.length }})
            </span>
          </button>
        </div>
      </section>

      <section class="detail-shell detail-content">
        <div class="detail-primary">
          <header class="property-heading">
            <div class="property-heading__badges">
              <span v-if="promotionLabel" class="promotion-badge">{{ promotionLabel }}</span>
              <span v-if="imovel.property_type">{{ imovel.property_type }}</span>
              <span v-if="imovel.condition">{{ imovel.condition }}</span>
            </div>
            <h1>{{ imovel.title }}</h1>
            <p class="property-location">
              <q-icon name="location_on" size="18px" />
              {{ imovel.location || "Localização não indicada" }}
            </p>
            <p class="property-price">
              {{ formatPrice(imovel.price, imovel.currency) }}<small v-if="priceSuffix">{{ priceSuffix }}</small>
            </p>
          </header>

          <section class="detail-block characteristics-section">
            <h2>Características</h2>
            <dl class="characteristics-grid">
              <div v-for="item in characteristicRows" :key="item.label">
                <dt>{{ item.label }}</dt>
                <dd>{{ item.value }}</dd>
              </div>
            </dl>
          </section>

          <section v-if="imovel.description" class="detail-block description-section">
            <h2>Descrição</h2>
            <div class="description-copy" v-html="imovel.description"></div>
          </section>

          <section v-if="imovel.location" class="detail-block location-section">
            <h2>Localização</h2>
            <div class="location-placeholder">
              <q-icon name="location_on" size="24px" />
              <strong>{{ imovel.location }}</strong>
              <span>A localização exacta é confirmada pelo agente responsável.</span>
            </div>
          </section>

          <section class="insurance-section" aria-labelledby="insurance-title">
            <div class="insurance-heading">
              <div>
                <span>SOLUÇÕES DE PARCEIROS SEGURADORES</span>
                <h2 id="insurance-title">Seguros que pode juntar ao contrato</h2>
              </div>
              <p>Protecção opcional para o imóvel, recheio e agregado familiar.</p>
            </div>
            <div class="insurance-grid">
              <article v-for="product in insuranceProducts" :key="product.title">
                <h3>{{ product.title }}</h3>
                <p>{{ product.description }}</p>
                <strong>{{ product.price }}</strong>
                <button type="button" @click="pedirProposta(product.title)">Pedir proposta</button>
              </article>
            </div>
          </section>
        </div>

        <aside class="detail-sidebar">
          <section class="contact-card">
            <div class="agent-row">
              <img :src="agent.photo || fallbackAgentImage" :alt="agent.name || 'Agente imobiliário'" />
              <div>
                <span>Agente responsável</span>
                <strong>{{ agent.name || "Golden Key" }}</strong>
                <small v-if="agent.email">{{ agent.email }}</small>
              </div>
            </div>

            <a v-if="agent.phone" class="contact-button contact-button--primary" :href="`tel:${agent.phone}`">
              Ligar: {{ agent.phone }}
            </a>
            <a
              v-if="agent.whatsapp"
              class="contact-button"
              :href="whatsAppUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp
            </a>
            <button class="contact-button contact-button--save" type="button" @click="toggleFavorito">
              <q-icon :name="favorito ? 'favorite' : 'favorite_border'" size="18px" />
              {{ favorito ? "Guardado nos favoritos" : "Guardar nos favoritos" }}
            </button>

            <dl class="contact-meta">
              <div v-if="referenceValue"><dt>Referência</dt><dd>{{ referenceValue }}</dd></div>
              <div><dt>Operação</dt><dd>{{ imovel.objective || "—" }}</dd></div>
            </dl>
          </section>

          <section class="partner-section">
            <span>PARCEIRO</span>
            <h2>Protecção para o seu imóvel</h2>
            <CarouselAd class="detail-partner-ad" variant="sidebar" placement-key="property_detail_sidebar" />
          </section>
        </aside>
      </section>

      <section v-if="relatedLoading || relatedProperties.length" class="detail-shell related-section">
        <div class="related-heading">
          <h2>Imóveis semelhantes</h2>
          <div class="related-heading__actions">
            <div class="related-slider-controls" aria-label="Navegar pelos imóveis semelhantes">
              <button type="button" aria-label="Imóveis semelhantes anteriores" @click.stop="scrollRelated(-1)">←</button>
              <button type="button" aria-label="Próximos imóveis semelhantes" @click.stop="scrollRelated(1)">→</button>
            </div>
            <button class="related-see-all" type="button" @click="router.push('/imoveis-search')">Ver todos os imóveis →</button>
          </div>
        </div>
        <div v-if="relatedLoading" class="related-grid related-grid--loading">
          <div v-for="n in 4" :key="n"></div>
        </div>
        <div
          v-else
          ref="relatedTrack"
          class="related-grid related-grid--carousel"
          :data-related-index="relatedIndex"
        >
          <article
            v-for="property in relatedProperties"
            :key="property.slug"
            tabindex="0"
            @click="openRelated(property)"
            @keydown.enter="openRelated(property)"
          >
            <div class="related-image">
              <img :src="getPropertyImage(property)" :alt="property.title" loading="lazy" />
              <span>{{ property.objective || property.property_type }}</span>
            </div>
            <div class="related-content">
              <strong>{{ formatPrice(property.price, property.currency) }}<small v-if="property.objective !== 'Venda'">/mês</small></strong>
              <h3>{{ property.title }}</h3>
              <p>{{ property.location }}</p>
              <div class="related-facts">
                <span v-if="property.bedroom_amount">{{ property.bedroom_amount }} quartos</span>
                <span v-if="property.bathroom_amount">{{ property.bathroom_amount }} WC</span>
                <span v-if="property.area">{{ property.area }} m²</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <q-dialog v-model="galeriaAberta" maximized>
        <q-card class="gallery-dialog bg-black">
          <q-bar class="bg-transparent">
            <q-space />
            <q-btn flat dense icon="close" color="white" v-close-popup aria-label="Fechar galeria" />
          </q-bar>
          <q-card-section class="gallery-dialog__content">
            <q-carousel v-model="imagemAtual" animated navigation infinite arrows control-color="primary" class="gallery-carousel">
              <q-carousel-slide v-for="(image, index) in images" :key="image" :name="index" :img-src="image" />
            </q-carousel>
          </q-card-section>
        </q-card>
      </q-dialog>
    </template>
  </main>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/config/api";
import store from "@/vuex/store";
import CarouselAd from "@/components/catalog/CarouselAd.vue";

const route = useRoute();
const router = useRouter();

const fallbackAgentImage = "https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/icon2.png";
const loading = ref(true);
const errorMessage = ref("");
const imovel = ref({ user: {}, imagens: [] });
const relatedProperties = ref([]);
const relatedLoading = ref(false);
const relatedTrack = ref(null);
const relatedIndex = ref(0);
const imagemAtual = ref(0);
const galeriaAberta = ref(false);
const favorito = ref(false);

const insuranceProducts = [
  { title: "Seguro de habitação", description: "Protecção do imóvel contra danos e imprevistos cobertos.", price: "Desde 450 MZN/mês" },
  { title: "Seguro de recheio", description: "Protecção para mobiliário, equipamentos e bens da habitação.", price: "Desde 320 MZN/mês" },
  { title: "Seguro de vida", description: "Segurança financeira adicional associada ao contrato do imóvel.", price: "Desde 1 100 MZN/mês" },
];

const agent = computed(() => imovel.value.user || {});
const images = computed(() => {
  const candidates = [
    imovel.value.primary_image_url,
    ...(imovel.value.imagens || []),
    ...(imovel.value.images || []),
    ...(imovel.value.property_images || []),
  ];
  return [...new Set(candidates.map((item) => typeof item === "string" ? item : item?.image_url || item?.url).filter(Boolean))];
});
const imagemPrincipal = computed(() => images.value[imagemAtual.value] || fallbackAgentImage);
const thumbnailImages = computed(() => images.value.slice(1, 5));
const priceSuffix = computed(() => imovel.value.objective && imovel.value.objective !== "Venda" ? "/mês" : "");
const referenceValue = computed(() => imovel.value.reference || imovel.value.code || imovel.value.property_code || "");
const promotionLabel = computed(() => imovel.value.promotion?.badge_label
  || (imovel.value.promotion?.editorial_featured ? "Destaque editorial" : ""));
const whatsAppUrl = computed(() => {
  const number = String(agent.value.whatsapp || "").replace(/\D/g, "");
  const message = encodeURIComponent(`Olá! Tenho interesse no imóvel ${imovel.value.title}.`);
  return `https://wa.me/${number}?text=${message}`;
});

const characteristicRows = computed(() => {
  const rows = [];
  const add = (label, value) => {
    if (value !== undefined && value !== null && value !== "") rows.push({ label, value });
  };
  add("Quartos", imovel.value.bedroom_amount);
  add("Casas de banho", imovel.value.bathroom_amount);
  add("Área do imóvel", imovel.value.area ? `${imovel.value.area} m²` : "");
  add("Lugares de garagem", imovel.value.garage_space ?? imovel.value.garage_spaces);
  add("Tipo de imóvel", imovel.value.property_type);
  add("Condição", imovel.value.condition);
  add("Operação", imovel.value.objective);
  add("Localização", imovel.value.location);
  add("Referência", referenceValue.value);
  return rows;
});

function unwrapCollection(data) {
  const payload = data?.data ?? data;
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.data) ? payload.data : [];
}

async function loadRelated() {
  relatedLoading.value = true;
  try {
    const catalog = await apiClient.get("/properties", { params: { per_page: 48 } });
    const seen = new Set();
    relatedProperties.value = unwrapCollection(catalog.data)
      .filter((property) => {
        if (!property?.slug || property.slug === imovel.value.slug || seen.has(property.slug)) return false;
        seen.add(property.slug);
        return true;
      })
      .map((property) => ({ property, score: propertySimilarityScore(property) }))
      .sort((left, right) => right.score - left.score
        || Number(right.property.promotion?.priority || 0) - Number(left.property.promotion?.priority || 0)
        || relativeDifference(left.property.price, imovel.value.price) - relativeDifference(right.property.price, imovel.value.price)
        || String(left.property.slug).localeCompare(String(right.property.slug)))
      .slice(0, 8)
      .map(({ property }) => property);
  } catch {
    relatedProperties.value = [];
  } finally {
    relatedLoading.value = false;
  }
}

function normalized(value) {
  return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
}

function relativeDifference(value, reference) {
  const first = Number(value);
  const second = Number(reference);
  if (!Number.isFinite(first) || !Number.isFinite(second) || second === 0) return Number.POSITIVE_INFINITY;
  return Math.abs(first - second) / Math.abs(second);
}

function propertySimilarityScore(property) {
  let score = 0;
  if (normalized(property.property_type) === normalized(imovel.value.property_type)) score += 5;
  if (normalized(property.objective) === normalized(imovel.value.objective)) score += 4;
  if (normalized(property.location) === normalized(imovel.value.location)) score += 3;
  if (Math.abs(Number(property.bedroom_amount) - Number(imovel.value.bedroom_amount)) <= 1) score += 2;
  const priceDifference = relativeDifference(property.price, imovel.value.price);
  if (priceDifference <= 0.25) score += 2;
  else if (priceDifference <= 0.5) score += 1;
  if (relativeDifference(property.area, imovel.value.area) <= 0.25) score += 1;
  if (normalized(property.condition) === normalized(imovel.value.condition)) score += 1;
  return score;
}

async function loadProperty() {
  loading.value = true;
  errorMessage.value = "";
  imagemAtual.value = 0;
  relatedIndex.value = 0;
  window.scrollTo(0, 0);
  try {
    const result = await store.dispatch("properties/getPropertyBySlug", route.params.slug);
    imovel.value = { ...result, user: result?.user || {}, imagens: result?.imagens || [] };
    const favorites = JSON.parse(localStorage.getItem("goldenkey:favorites") || "[]");
    favorito.value = favorites.includes(imovel.value.slug);
    await loadRelated();
  } catch {
    errorMessage.value = "Não foi possível carregar este imóvel. Tente novamente.";
  } finally {
    loading.value = false;
  }
}

function formatPrice(price, currency) {
  if (price === undefined || price === null || price === "") return "Preço sob consulta";
  return new Intl.NumberFormat("pt-MZ", { style: "currency", currency: currency || "MZN", maximumFractionDigits: 0 }).format(Number(price));
}

function getPropertyImage(property) {
  return property.primary_image_url || property.imagens?.[0] || property.images?.[0] || fallbackAgentImage;
}

function selecionarImagem(index) { imagemAtual.value = index; }
function imagemAnterior() { if (images.value.length) imagemAtual.value = (imagemAtual.value - 1 + images.value.length) % images.value.length; }
function proximaImagem() { if (images.value.length) imagemAtual.value = (imagemAtual.value + 1) % images.value.length; }
function abrirGaleria(index) { imagemAtual.value = index; galeriaAberta.value = true; }
function voltarLista() { window.history.state?.back ? router.back() : router.push("/imoveis-search"); }
function openRelated(property) { router.push({ name: "imovel-details", params: { slug: property.slug } }); }
function scrollRelated(direction) {
  const track = relatedTrack.value;
  if (!track) return;
  const firstCard = track.querySelector("article");
  const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 20;
  const cardWidth = firstCard?.getBoundingClientRect().width || track.clientWidth;
  const visibleCards = window.innerWidth > 1100 ? 4 : window.innerWidth > 600 ? 2 : 1;
  const lastIndex = Math.max(0, relatedProperties.value.length - visibleCards);
  relatedIndex.value = Math.min(lastIndex, Math.max(0, relatedIndex.value + direction * visibleCards));
  track.scrollTo({ left: relatedIndex.value * (cardWidth + gap), behavior: "smooth" });
}

function toggleFavorito() {
  const favorites = new Set(JSON.parse(localStorage.getItem("goldenkey:favorites") || "[]"));
  if (favorito.value) favorites.delete(imovel.value.slug);
  else favorites.add(imovel.value.slug);
  favorito.value = !favorito.value;
  localStorage.setItem("goldenkey:favorites", JSON.stringify([...favorites]));
}

function pedirProposta(product) {
  localStorage.setItem("supportSubject", `Seguro — ${product}`);
  router.push({ name: "suporte", query: { assunto: "seguro", produto: product } });
}

watch(() => route.params.slug, loadProperty, { immediate: true });
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Karla:wght@400;500;600;700&family=Sora:wght@600;700&display=swap");

.property-detail-page { min-height: 100vh; padding: 16px 0 72px; color: #14110e; background: #fff; font-family: "Karla", sans-serif; }
.detail-shell { box-sizing: border-box; width: min(100%, 1440px); margin-inline: auto; padding-inline: 48px; }
.breadcrumbs { display: flex; align-items: center; gap: 8px; min-height: 34px; overflow: hidden; color: #756e65; font-size: 12px; white-space: nowrap; }
.breadcrumbs button { padding: 0; border: 0; color: #7b6134; background: none; cursor: pointer; }
.breadcrumbs span:last-child { overflow: hidden; text-overflow: ellipsis; }

.gallery-section { display: grid; grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr); grid-template-rows: 502px; gap: 12px; margin-top: 8px; }
.gallery-section--single { grid-template-columns: 1fr; }
.gallery-main, .gallery-thumbnails button { position: relative; min-width: 0; padding: 0; border: 0; border-radius: 5px; background: #c9c2b6; overflow: hidden; cursor: pointer; }
.gallery-main > img, .gallery-thumbnails img { width: 100%; height: 100%; display: block; object-fit: cover; }
.gallery-thumbnails { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: repeat(2, minmax(0, 1fr)); gap: 12px; min-width: 0; }
.gallery-thumbnails button.is-active { box-shadow: inset 0 0 0 3px #eba301; }
.gallery-badges { position: absolute; left: 14px; bottom: 14px; display: flex; gap: 8px; }
.gallery-badges span { padding: 6px 11px; border-radius: 999px; color: #fff; background: #14110e; font-size: 11px; line-height: 14px; font-weight: 600; }
.gallery-badges .gallery-badge--light { color: #14110e; background: #fff; }
.gallery-badges .promotion-badge, .property-heading__badges .promotion-badge { color: #14110e; background: #eba301; }
.gallery-arrows { position: absolute; inset: 0 14px; display: flex; align-items: center; justify-content: space-between; pointer-events: none; }
.gallery-arrows span { display: grid; place-items: center; width: 38px; height: 38px; border-radius: 50%; color: #14110e; background: #fff; box-shadow: 0 2px 8px rgba(20,17,14,.18); font-size: 27px; line-height: 1; pointer-events: auto; }
.gallery-more { position: absolute; inset: auto 10px 10px auto; padding: 7px 10px; border-radius: 5px; color: #fff; background: rgba(20,17,14,.8); font-size: 11px; font-weight: 600; }

.detail-content { display: grid; grid-template-columns: minmax(0, 972px) minmax(280px, 332px); gap: 40px; align-items: start; padding-top: 32px; }
.detail-primary { display: grid; gap: 40px; min-width: 0; }
.property-heading { padding-bottom: 24px; border-bottom: 1px solid #e6e0d6; }
.property-heading__badges { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.property-heading__badges span { padding: 5px 10px; border-radius: 999px; color: #fff; background: #14110e; font-size: 11px; line-height: 14px; font-weight: 600; }
.property-heading h1 { max-width: 820px; margin: 0; font-family: "Sora", sans-serif; font-size: clamp(25px, 2.1vw, 34px); line-height: 1.22; letter-spacing: -.02em; }
.property-location { display: flex; align-items: center; gap: 5px; margin: 10px 0 18px; color: #5a5147; font-size: 14px; }
.property-price { margin: 0; font-family: "Sora", sans-serif; font-size: 30px; line-height: 1.2; font-weight: 700; }
.property-price small { margin-left: 5px; color: #5a5147; font-family: "Karla", sans-serif; font-size: 14px; font-weight: 400; }
.detail-block { min-width: 0; }
.detail-block h2, .related-heading h2 { margin: 0 0 18px; font-family: "Sora", sans-serif; font-size: 20px; line-height: 26px; font-weight: 700; }
.characteristics-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); margin: 0; border-top: 1px solid #e6e0d6; }
.characteristics-grid > div { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 16px; padding: 13px 0; border-bottom: 1px solid #e6e0d6; }
.characteristics-grid > div:nth-child(odd) { padding-right: 28px; }
.characteristics-grid > div:nth-child(even) { padding-left: 28px; border-left: 1px solid #e6e0d6; }
.characteristics-grid dt { color: #756e65; font-size: 13px; }
.characteristics-grid dd { margin: 0; text-align: right; font-size: 13px; font-weight: 600; overflow-wrap: anywhere; }
.description-copy { color: #5a5147; font-size: 14px; line-height: 1.7; }
.description-copy :deep(p) { margin: 0 0 12px; }
.location-placeholder { box-sizing: border-box; min-height: 260px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 28px; color: #5a5147; background: #c9c2b6; text-align: center; }
.location-placeholder strong { color: #14110e; font-size: 16px; }
.location-placeholder span { max-width: 420px; font-size: 12px; }

.detail-sidebar { position: sticky; top: 20px; display: grid; gap: 18px; min-width: 0; }
.contact-card { padding: 20px; border: 1px solid #e6e0d6; border-radius: 5px; background: #fff; box-shadow: 0 2px 8px rgba(20,17,14,.08); }
.agent-row { display: grid; grid-template-columns: 58px minmax(0, 1fr); gap: 12px; align-items: center; padding-bottom: 18px; }
.agent-row img { width: 58px; height: 58px; border-radius: 50%; object-fit: cover; background: #f4f0e8; }
.agent-row div { min-width: 0; display: grid; gap: 3px; }
.agent-row span, .agent-row small { overflow: hidden; color: #756e65; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.agent-row strong { overflow: hidden; font-family: "Sora", sans-serif; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; }
.contact-button { box-sizing: border-box; width: 100%; min-height: 42px; display: flex; align-items: center; justify-content: center; gap: 7px; margin-top: 10px; padding: 0 14px; border: 1px solid #7b6134; border-radius: 5px; color: #7b6134; background: #fff; font: 600 13px/16px "Karla", sans-serif; text-decoration: none; cursor: pointer; }
.contact-button--primary { border-color: #eba301; color: #14110e; background: #eba301; }
.contact-button--save { border-color: #e6e0d6; color: #14110e; }
.contact-meta { display: grid; gap: 9px; margin: 18px 0 0; padding-top: 16px; border-top: 1px solid #e6e0d6; }
.contact-meta div { display: flex; justify-content: space-between; gap: 12px; font-size: 11px; }
.contact-meta dt { color: #756e65; }
.contact-meta dd { margin: 0; font-weight: 600; text-align: right; }
.partner-section { padding: 18px; border: 1px solid #e6e0d6; border-radius: 5px; background: #f4f0e8; }
.partner-section > span, .insurance-heading span { color: #a87300; font-size: 10px; line-height: 13px; font-weight: 700; letter-spacing: .13em; }
.partner-section h2 { margin: 6px 0 14px; font-family: "Sora", sans-serif; font-size: 16px; line-height: 22px; }
.detail-partner-ad { aspect-ratio: 3 / 2 !important; min-height: 0 !important; border-radius: 3px; }

.insurance-section { box-sizing: border-box; min-height: 332px; padding: 28px; border-radius: 5px; color: #fff; background: #14110e; }
.insurance-heading { display: flex; align-items: end; justify-content: space-between; gap: 24px; margin-bottom: 18px; }
.insurance-heading h2 { margin: 6px 0 0; font-family: "Sora", sans-serif; font-size: 20px; line-height: 26px; }
.insurance-heading > p { max-width: 330px; margin: 0; color: #c9c2b6; font-size: 12px; line-height: 17px; }
.insurance-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.insurance-grid article { min-width: 0; min-height: 184px; display: flex; flex-direction: column; padding: 18px; border: 1px solid #3f3933; border-radius: 5px; background: #211d19; }
.insurance-grid h3 { margin: 0 0 8px; font-family: "Sora", sans-serif; font-size: 14px; line-height: 19px; }
.insurance-grid p { margin: 0 0 15px; color: #c9c2b6; font-size: 11px; line-height: 16px; }
.insurance-grid strong { margin-top: auto; color: #eba301; font-size: 13px; }
.insurance-grid button { align-self: flex-start; margin-top: 7px; padding: 0; border: 0; color: #eba301; background: none; font: 600 11px/15px "Karla", sans-serif; cursor: pointer; }

.related-section { padding-top: 34px; }
.related-heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 18px; }
.related-heading h2 { margin: 0; }
.related-heading__actions,.related-slider-controls { display:flex; align-items:center; }
.related-heading__actions { gap:12px; }
.related-slider-controls { gap:7px; }
.related-slider-controls button { width:36px; height:36px; display:grid; place-items:center; padding:0; border:1px solid #d8d1c6; border-radius:50%; color:#14110e; background:#fff; font:600 17px/1 "Karla",sans-serif; cursor:pointer; }
.related-slider-controls button:hover,.related-slider-controls button:focus-visible { border-color:#7b6134; color:#fff; background:#7b6134; outline:none; }
.related-heading .related-see-all { min-height: 36px; padding: 0 14px; border: 1px solid #7b6134; border-radius: 10px; color: #7b6134; background: transparent; font: 600 12px/14px "Karla", sans-serif; cursor: pointer; }
.related-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 20px; }
.related-grid--carousel { grid-template-columns:none; grid-auto-flow:column; grid-auto-columns:calc((100% - 60px) / 4); overflow-x:auto; overscroll-behavior-inline:contain; scroll-behavior:smooth; scroll-snap-type:inline mandatory; scrollbar-width:none; }
.related-grid--carousel::-webkit-scrollbar { display:none; }
.related-grid--carousel > article { scroll-snap-align:start; }
.related-grid > article { min-width: 0; border: 1px solid #e6e0d6; border-radius: 5px; background: #fff; box-shadow: 0 2px 8px rgba(20,17,14,.08); overflow: hidden; cursor: pointer; }
.related-image { position: relative; aspect-ratio: 4 / 3; overflow: hidden; background: #c9c2b6; }
.related-image img { width: 100%; height: 100%; display: block; object-fit: cover; }
.related-image span { position: absolute; left: 10px; top: 10px; padding: 5px 9px; border-radius: 999px; color: #fff; background: #14110e; font-size: 10px; font-weight: 600; }
.related-content { padding: 13px; }
.related-content > strong { font-family: "Sora", sans-serif; font-size: 17px; line-height: 22px; }
.related-content > strong small { margin-left: 3px; font: 400 11px/14px "Karla", sans-serif; }
.related-content h3 { margin: 8px 0 2px; overflow: hidden; font-size: 14px; line-height: 18px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.related-content p { margin: 0; overflow: hidden; color: #756e65; font-size: 12px; line-height: 16px; text-overflow: ellipsis; white-space: nowrap; }
.related-facts { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 10px; }
.related-facts span { padding: 4px 6px; border-radius: 3px; color: #5a5147; background: #f4f0e8; font-size: 9px; }
.related-grid--loading > div, .loading-gallery, .loading-panel { background: linear-gradient(90deg, #e6e0d6, #f4f0e8, #e6e0d6); background-size: 200% 100%; animation: shimmer 1.5s linear infinite; }
.related-grid--loading > div { aspect-ratio: .75; border-radius: 5px; }
.detail-loading { padding-top: 36px; }
.loading-gallery { height: 502px; border-radius: 5px; }
.loading-grid { display: grid; grid-template-columns: minmax(0, 972px) minmax(280px, 332px); gap: 40px; margin-top: 32px; }
.loading-panel { height: 620px; border-radius: 5px; }
.loading-panel--side { height: 300px; }
.detail-error { min-height: 420px; display: grid; place-content: center; justify-items: center; gap: 14px; text-align: center; }
.detail-error button { min-height: 42px; padding: 0 18px; border: 0; border-radius: 5px; color: #14110e; background: #eba301; font-weight: 700; cursor: pointer; }
.gallery-dialog__content { height: calc(100vh - 50px); display: grid; place-items: center; }
.gallery-carousel { width: min(92vw, 1400px); height: 84vh; }
.gallery-carousel :deep(.q-carousel__slide) { background-size: contain; background-repeat: no-repeat; }
@keyframes shimmer { to { background-position: -200% 0; } }

@media (max-width: 1100px) {
  .detail-shell { padding-inline: 24px; }
  .gallery-section { grid-template-rows: 420px; }
  .detail-content { grid-template-columns: minmax(0, 1fr) 300px; gap: 24px; }
  .related-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .related-grid--carousel { grid-template-columns:none; grid-auto-columns:calc((100% - 20px) / 2); }
}

@media (max-width: 820px) {
  .gallery-section { grid-template-columns: 1fr; grid-template-rows: minmax(300px, 58vw) 150px; }
  .gallery-section--single { grid-template-rows: minmax(300px, 58vw); }
  .gallery-thumbnails { grid-template-columns: repeat(4, minmax(0, 1fr)); grid-template-rows: 1fr; }
  .detail-content, .loading-grid { grid-template-columns: 1fr; }
  .detail-primary { display: contents; }
  .property-heading { grid-row: 1; }
  .detail-sidebar { position: static; grid-row: 2; }
  .characteristics-section { grid-row: 3; }
  .description-section { grid-row: 4; }
  .location-section { grid-row: 5; }
  .insurance-section { grid-row: 6; }
  .partner-section { display: block; }
}

@media (max-width: 600px) {
  .property-detail-page { padding-top: 8px; padding-bottom: 48px; }
  .detail-shell { padding-inline: 16px; }
  .breadcrumbs { min-height: 38px; }
  .gallery-section { grid-template-rows: 64vw 82px; gap: 8px; margin-top: 2px; }
  .gallery-section--single { grid-template-rows: 64vw; }
  .gallery-thumbnails { gap: 8px; }
  .gallery-thumbnails button:nth-child(n+4) { display: none; }
  .gallery-thumbnails { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .gallery-badges { left: 10px; bottom: 10px; }
  .gallery-arrows { display: none; }
  .detail-content { gap: 28px; padding-top: 24px; }
  .detail-primary { gap: 32px; }
  .property-heading h1 { font-size: 23px; }
  .property-price { font-size: 25px; }
  .characteristics-grid { grid-template-columns: 1fr; }
  .characteristics-grid > div:nth-child(odd), .characteristics-grid > div:nth-child(even) { padding: 12px 0; border-left: 0; }
  .insurance-section { padding: 22px 16px; }
  .insurance-heading { align-items: start; flex-direction: column; }
  .insurance-grid { grid-template-columns: 1fr; }
  .insurance-grid article { min-height: 150px; }
  .related-heading { align-items: flex-start; flex-direction:column; }
  .related-heading__actions { width:100%; justify-content:space-between; }
  .related-heading .related-see-all { font-size: 10px; }
  .related-grid { grid-template-columns: 1fr; }
  .related-grid--carousel { grid-template-columns:none; grid-auto-columns:calc(100% - 36px); }
}

@media (prefers-reduced-motion: reduce) {
  .related-grid--loading > div, .loading-gallery, .loading-panel { animation: none; }
}
</style>
